import React from 'react';
import { Info, Briefcase, Package, AlertTriangle, ShieldCheck, Ruler, FileText, Globe } from 'lucide-react';

/**
 * Компонент с информацией о правилах перевозки багажа
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-purple-500" />
        Правила перевозки багажа в самолетах
      </h2>
      
      <div className="prose prose-purple dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-purple-500" />
          Что такое багаж и ручная кладь?
        </h3>
        <p>
          <strong>Регистрируемый багаж</strong> — это ваши вещи, которые перевозятся в багажном отделении самолета. 
          Вы сдаете их при регистрации на рейс, и авиакомпания несет ответственность за их сохранность.
        </p>
        <p>
          <strong>Ручная кладь</strong> — это небольшие сумки и предметы, которые вы берете с собой в салон самолета.
          Ручная кладь остается под вашим контролем в течение всего полета.
        </p>

        <h3 className="flex items-center gap-2">
          <Ruler className="w-5 h-5 text-purple-500" />
          Нормы провоза багажа в России
        </h3>
        <p>
          Нормы багажа определяются авиакомпанией и зависят от класса обслуживания, направления полета и вашего статуса в программе лояльности.
          Типичные нормы для российских авиакомпаний:
        </p>
        <ul>
          <li><strong>Эконом-класс:</strong> 1 место багажа до 23 кг, ручная кладь до 10 кг</li>
          <li><strong>Комфорт-класс:</strong> 1 место багажа до 23 кг, ручная кладь до 10 кг</li>
          <li><strong>Бизнес-класс:</strong> 2 места багажа до 32 кг каждое, ручная кладь до 15 кг</li>
        </ul>
        <p>
          Обратите внимание, что некоторые лоукостеры (например, "Победа") имеют более строгие правила и могут взимать дополнительную плату даже за первое место багажа.
        </p>

        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800 my-6">
          <h4 className="text-purple-700 dark:text-purple-300 font-medium">Важно знать о габаритах</h4>
          <p className="text-purple-600 dark:text-purple-400 text-sm mb-0">
            Стандартные габариты для ручной клади в российских авиакомпаниях обычно не превышают <strong>55×40×25 см</strong> (сумма измерений до 115 см).
            Для сдаваемого багажа общий размер (сумма длины, ширины и высоты) обычно не должен превышать <strong>158 см</strong>.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Package className="w-5 h-5 text-purple-500" />
          Дополнительный багаж и перевес
        </h3>
        <p>
          Если вы планируете взять больше вещей, чем разрешено по вашему тарифу, у вас есть несколько вариантов:
        </p>
        <ul>
          <li><strong>Оплата дополнительного места багажа:</strong> Можно оплатить провоз дополнительного чемодана или сумки</li>
          <li><strong>Оплата перевеса:</strong> Если ваш багаж тяжелее нормы, можно оплатить перевес</li>
          <li><strong>Предварительное бронирование:</strong> Оплата дополнительного багажа онлайн при бронировании билета обычно дешевле, чем в аэропорту</li>
        </ul>
        <p>
          Стоимость перевеса багажа в России варьируется от 2000 до 5000 рублей за одно дополнительное место багажа на внутренних рейсах
          и от 3000 до 7000 рублей на международных рейсах, в зависимости от авиакомпании.
        </p>

        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Особые категории багажа
        </h3>
        <p>
          Для некоторых предметов действуют особые правила перевозки:
        </p>
        <ul>
          <li><strong>Спортивный инвентарь:</strong> Лыжи, сноуборды, велосипеды, гольф-оборудование часто требуют дополнительной оплаты и специальной упаковки</li>
          <li><strong>Музыкальные инструменты:</strong> Могут перевозиться как ручная кладь, если соответствуют габаритам, или как зарегистрированный багаж</li>
          <li><strong>Медицинское оборудование:</strong> Часто разрешается бесплатно при наличии медицинских документов</li>
          <li><strong>Детские коляски:</strong> Обычно перевозятся бесплатно и не включаются в норму багажа</li>
        </ul>

        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800 my-6">
          <h4 className="text-amber-700 dark:text-amber-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Запрещенные предметы
          </h4>
          <p className="text-amber-600 dark:text-amber-400 text-sm mb-2">
            <strong>В ручной клади запрещено провозить:</strong>
          </p>
          <ul className="text-amber-600 dark:text-amber-400 text-sm mb-2">
            <li>Острые и режущие предметы (ножи, ножницы с лезвиями более 6 см)</li>
            <li>Жидкости в емкостях более 100 мл (общий объем до 1 л в прозрачном пакете)</li>
            <li>Воспламеняющиеся вещества (зажигалки - не более одной на человека)</li>
            <li>Оружие или его имитации</li>
          </ul>
          <p className="text-amber-600 dark:text-amber-400 text-sm mb-0">
            <strong>В багаже запрещено провозить:</strong> взрывчатые и легковоспламеняющиеся вещества, 
            сжатые газы, токсичные материалы, радиоактивные материалы.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-green-500" />
          Как защитить свой багаж
        </h3>
        <ol>
          <li><strong>Используйте прочный чемодан</strong> с надежными замками</li>
          <li><strong>Отмечайте багаж</strong> яркими бирками или наклейками для быстрой идентификации</li>
          <li><strong>Сделайте фотографию</strong> своего багажа перед сдачей</li>
          <li><strong>Упакуйте ценные вещи</strong> в ручную кладь</li>
          <li><strong>Зафиксируйте данные о багаже</strong> — сохраните багажную бирку</li>
          <li><strong>Рассмотрите страхование багажа</strong> для дорогостоящих предметов</li>
        </ol>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" />
          Документы авиакомпаний
        </h3>
        <p>
          Официальные правила перевозки багажа можно найти:
        </p>
        <ul>
          <li>На официальных сайтах авиакомпаний в разделах "Багаж" или "Правила перевозки"</li>
          <li>В мобильных приложениях авиакомпаний</li>
          <li>В тексте договора перевозки при покупке билета</li>
          <li>При регистрации на рейс в аэропорту (информационные стенды)</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-500" />
          Международные перелеты и правила разных стран
        </h3>
        <p>
          При международных перелетах учитывайте:
        </p>
        <ul>
          <li><strong>Таможенные правила</strong> страны назначения (ограничения на ввоз продуктов, лекарств, алкоголя и т.д.)</li>
          <li><strong>Различия в нормах</strong> багажа при стыковочных рейсах разными авиакомпаниями</li>
          <li><strong>Возможные дополнительные проверки</strong> в странах со строгими правилами безопасности</li>
        </ul>
        <p>
          Для поездок в Европу, США, Китай и другие страны рекомендуется заранее ознакомиться с их особыми требованиями к багажу.
        </p>

        <p className="font-semibold mt-6">
          Наш калькулятор поможет вам спланировать перевозку багажа и избежать непредвиденных расходов в аэропорту!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;